import { useEffect } from "react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { LEGAL } from "@/config/legal";

const Terms = () => {
  useEffect(() => {
    document.title = "Terms of Service | Decision Sales OS";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Terms of Service for Decision Sales OS. Read our terms and conditions for using the platform.");
    }
  }, []);

  return (
    <PublicLayout showCookieBanner={false}>
      <h1 className="text-3xl font-semibold tracking-tight mb-4">Terms of Service</h1>
      <p className="text-muted-foreground mb-10">Last updated: December 2024</p>
      <div className="prose prose-neutral dark:prose-invert max-w-none">
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">1. Acceptance of Terms</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          By accessing or using Decision Sales OS ("the Service"), you agree to be bound by these 
          Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          These Terms apply to all users of the Service, including individual users, team members, 
          and administrators of organizational accounts.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">2. Description of Service</h2>
        <p className="text-muted-foreground leading-relaxed">
          Decision Sales OS is a sales operating system that provides lead management, pipeline 
          tracking, calendar scheduling, and decision-support tools. The Service is provided 
          "as is" and we reserve the right to modify, suspend, or discontinue any aspect of the 
          Service at any time.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">3. User Accounts</h2>
        <h3 className="font-medium text-foreground mb-3 mt-6">3.1 Account Creation</h3>
        <p className="text-muted-foreground leading-relaxed mb-4">
          You must provide accurate and complete information when creating an account. You are 
          responsible for maintaining the confidentiality of your account credentials and for 
          all activities that occur under your account.
        </p>
        <h3 className="font-medium text-foreground mb-3">3.2 Account Security</h3>
        <p className="text-muted-foreground leading-relaxed">
          You must immediately notify us of any unauthorized use of your account or any other 
          breach of security. We are not liable for any loss or damage arising from your failure 
          to protect your account credentials.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">4. Acceptable Use</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">You agree not to:</p>
        <ul className="space-y-2 text-muted-foreground">
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Use the Service for any unlawful purpose or in violation of applicable laws</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Attempt to gain unauthorized access to any part of the Service</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Interfere with or disrupt the integrity or performance of the Service</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Transmit any malicious code, viruses, or harmful content</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Resell, sublicense, or redistribute the Service without authorization</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
            <span>Use automated means to access the Service without our prior written consent</span>
          </li>
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">5. Intellectual Property</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          The Service, including all content, features, and functionality, is owned by Decision 
          Sales OS and is protected by copyright, trademark, and other intellectual property laws.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          You retain ownership of all data you submit to the Service. By using the Service, you 
          grant us a limited license to use, store, and process your data solely for the purpose 
          of providing the Service to you.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">6. Payment Terms</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          If you subscribe to a paid plan, you agree to pay all fees in accordance with the 
          pricing and billing terms in effect at the time of purchase. Fees are non-refundable 
          except as required by law or as explicitly stated otherwise.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          We reserve the right to change our pricing at any time. Any price changes will be 
          communicated in advance and will apply to subsequent billing periods.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">7. Termination</h2>
        <p className="text-muted-foreground leading-relaxed mb-4">
          You may terminate your account at any time by contacting us or through the account 
          settings. We may terminate or suspend your account at any time for any reason, 
          including violation of these Terms.
        </p>
        <p className="text-muted-foreground leading-relaxed">
          Upon termination, your right to use the Service will immediately cease. We will 
          retain your data for a reasonable period to allow for export, after which it will 
          be deleted in accordance with our data retention policies.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">8. Disclaimer of Warranties</h2>
        <p className="text-muted-foreground leading-relaxed">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
          EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES 
          OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT 
          WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">9. Limitation of Liability</h2>
        <p className="text-muted-foreground leading-relaxed">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, DECISION SALES OS SHALL NOT BE LIABLE FOR ANY 
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS 
          OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, 
          OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE SERVICE.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl font-semibold text-foreground mb-4">10. Changes to Terms</h2>
        <p className="text-muted-foreground leading-relaxed">
          We reserve the right to modify these Terms at any time. We will provide notice of 
          significant changes by posting the updated Terms on the Service and updating the 
          "Last updated" date. Your continued use of the Service after such changes constitutes 
          acceptance of the modified Terms.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-foreground mb-4">11. Contact</h2>
        <p className="text-muted-foreground leading-relaxed">
          If you have questions about these Terms, please contact us at{" "}
          <a 
            href={`mailto:${LEGAL.legalEmail}`} 
            className="text-primary hover:text-primary/80 transition-colors"
          >
            {LEGAL.legalEmail}
          </a>
          . {LEGAL.companyName}, {LEGAL.address}. P.IVA/VAT: {LEGAL.pivaVat}.
        </p>
      </section>
      </div>
    </PublicLayout>
  );
};

export default Terms;
